import {
  type ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ANIMATION_DURATION } from '../constants';
import type { SlideOutProps, SlideOutRef, SlideOutStatus } from '../types';
import { playCloseAnimation } from '../utils/playCloseAnimation';
import { playOpenAnimation } from '../utils/playOpenAnimation';
import { flushResolvers, waitForResolvers } from '../utils/statusResolvers';
import { useCanClose } from './useCanClose';

interface UseSlideOutControllerOptions {
  ref: ForwardedRef<SlideOutRef>;
  panelWidth: string;
  side: NonNullable<SlideOutProps['side']>;
  onStatusChange?: SlideOutProps['onStatusChange'];
  onBeforeClose?: SlideOutProps['onBeforeClose'];
}

/**
 * Owns the panel lifecycle: isClosed → isOpening → isOpen → isClosing.
 *
 * `open()` and `close()` return promises that resolve when that transition
 * finishes. A second call while one is in flight waits on the same promise
 * instead of starting another animation.
 */
export function useSlideOutController({
  ref,
  panelWidth,
  side,
  onStatusChange,
  onBeforeClose,
}: UseSlideOutControllerOptions) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<SlideOutStatus>('isClosed');

  // Read inside callbacks so they always see the latest status without
  // being recreated on every status change.
  const statusRef = useRef<SlideOutStatus>(status);
  const openResolversRef = useRef<(() => void)[]>([]);
  const closeResolversRef = useRef<(() => void)[]>([]);
  const closeAnimationCleanupRef = useRef<(() => void) | null>(null);

  statusRef.current = status;

  const canClose = useCanClose(onBeforeClose);

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // The panel node is created only after `isOpen` becomes true, so both the
  // ref (for the exit transition) and state (to start the enter transition)
  // have to be set from the callback ref.
  const setContentRef = useCallback((element: HTMLDivElement | null) => {
    contentRef.current = element;
    setContentElement(element);
  }, []);

  useLayoutEffect(() => {
    if (!contentElement) {
      return;
    }

    return playOpenAnimation(contentElement, ANIMATION_DURATION, () => {
      // Ignore the timer if the panel already started closing.
      setStatus((prevStatus) =>
        prevStatus === 'isOpening' ? 'isOpen' : prevStatus,
      );
    });
  }, [contentElement]);

  // `open()` also resolves when the panel closes mid-open, otherwise that
  // promise would wait forever for `isOpen`.
  useEffect(() => {
    if (
      status === 'isOpen' ||
      status === 'isClosing' ||
      status === 'isClosed'
    ) {
      flushResolvers(openResolversRef);
    }

    if (status === 'isClosed') {
      flushResolvers(closeResolversRef);
    }
  }, [status]);

  const closeSlideOut = useCallback(() => {
    setStatus('isClosed');
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(async () => {
    if (statusRef.current === 'isOpen') {
      return;
    }

    if (statusRef.current === 'isOpening') {
      await waitForResolvers(openResolversRef);

      return;
    }

    setStatus((prevStatus) => {
      if (['isOpen', 'isOpening'].includes(prevStatus)) {
        return prevStatus;
      }

      return 'isOpening';
    });

    setIsOpen(true);

    await waitForResolvers(openResolversRef);
  }, []);

  const handleClose = useCallback(async () => {
    if (statusRef.current === 'isClosed') {
      return;
    }

    if (statusRef.current === 'isClosing') {
      await waitForResolvers(closeResolversRef);

      return;
    }

    setStatus('isClosing');

    closeAnimationCleanupRef.current = playCloseAnimation({
      overlay: overlayRef.current,
      content: contentRef.current,
      side,
      panelWidth,
      onClosed: () => {
        closeAnimationCleanupRef.current = null;
        closeSlideOut();
      },
    });

    await waitForResolvers(closeResolversRef);
  }, [panelWidth, side, closeSlideOut]);

  // Drop a pending exit timer if the panel unmounts while already closed.
  // While it is open, Radix still owns the node and the timer should run.
  useEffect(() => {
    return () => {
      if (!isOpen) {
        closeAnimationCleanupRef.current?.();
        closeAnimationCleanupRef.current = null;
      }
    };
  }, [isOpen]);

  useImperativeHandle(
    ref,
    () => ({
      close: handleClose,
      open: handleOpen,
    }),
    [handleClose, handleOpen],
  );

  // Radix reports every dismissal here, including the ones `onBeforeClose`
  // is allowed to veto. An in-flight or finished close is ignored so Radix's
  // own `onOpenChange(false)` cannot start a second exit.
  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (open) {
        void handleOpen();

        return;
      }

      if (
        statusRef.current === 'isClosing' ||
        statusRef.current === 'isClosed'
      ) {
        return;
      }

      if (await canClose()) {
        void handleClose();
      }
    },
    [handleOpen, handleClose, canClose],
  );

  return {
    isOpen,
    overlayRef,
    setContentRef,
    handleOpenChange,
  };
}
